import React, {
  useEffect, useState, useRef, useCallback
} from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import cx from 'classnames';

import AutoLogoutModal from '../Modal/AutoLogoutModal';
import ChangePasswordModal from '../Modal/ChangePasswordModal';
import StartPageModal from '../StartPage/StartPageModal';
import Header from '../Header/Header';
import api from '../../api';
import UploadingTools from '../Extensions/UploadingTools';

import './Layout.css';

const REFRESH_TIME = 1000 * 60 * 120; // 120 minutes
const EXTEND_TIME = 1000 * 60 * 120; // 120 minutes

function Layout(props) {
  const [isOpenStartPage, setStartPage] = useState(false);
  const [isActiveTimer, setActiveTimer] = useState(false);
  const [isOpenMCP, setMCP] = useState(false);
  const [isExpirationPass, setExpirationPass] = useState(false);

  const updateIntervalRef = useRef(null);
  const updatingTimerRef = useRef(false);

  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;

  const { state } = props;
  const { user, company } = state;

  const requireLogin = useCallback(async () => {
    try {
      await user.getValidSession();
    } catch {
      localStorage.removeItem('session');
      localStorage.removeItem('main_page');
      navigate('/login', { replace: true });
    }
  }, [user, navigate]);

  const refreshToken = useCallback(() => {
    const storageSession = JSON.parse(localStorage.getItem('session'));
    const refreshCycle = localStorage.getItem('refreshCycle');

    if (storageSession && refreshCycle) {
      const now = Date.now();
      const requiredTime = storageSession.created_at * 1000 + REFRESH_TIME;

      if (now > requiredTime) {
        localStorage.removeItem('refreshCycle');
        api.refreshToken(storageSession.refresh_token)
          .then((response) => {
            if (!response || response.error) {
              return Promise.reject(new Error('Token refresh failed'));
            }
            localStorage.setItem('refreshCycle', '1');
            return user.setSession(response);
          })
          .catch((err) => {
            console.error('Error refreshing token:', err);
          });
      }
    }
  }, [user]);

  const onRefreshStop = useCallback(() => {
    if (updateIntervalRef.current) {
      clearTimeout(updateIntervalRef.current);
      updateIntervalRef.current = null;
    }
  }, []);

  const getExtendToken = useCallback(() => {
    api.getExtendToken()
      .then(() => {
        setActiveTimer(false);
        updatingTimerRef.current = false;
      })
      .catch((err) => {
        setActiveTimer(false);
        updatingTimerRef.current = false;
        console.error('Error getting extend token:', err);
      });
  }, []);

  const onRefreshStart = useCallback(() => {
    if (!isActiveTimer && !updatingTimerRef.current) {
      setActiveTimer(true);
      updatingTimerRef.current = true;
      onRefreshStop();
      updateIntervalRef.current = setTimeout(() => {
        getExtendToken();
      }, EXTEND_TIME);
    }
  }, [isActiveTimer, onRefreshStop, getExtendToken]);

  const checkClaritySettings = useCallback(() => {
    let required_setting;
    const required_setting_string = localStorage.getItem('required_setting');
    if (required_setting_string) {
      required_setting = required_setting_string.split(',');
    } else {
      required_setting = [];
    }

    if (required_setting.some((setting) => { return ['ip', 'email', 'license'].includes(setting); })) {
      company.fetchCompanySettings();
      setStartPage(true);
    }

    if (required_setting.length === 1 && required_setting.includes('password')) {
      setMCP(true);
      setExpirationPass(true);
    }
  }, [company]);

  const destroySession = useCallback(() => {
    localStorage.removeItem('session');
    localStorage.removeItem('main_page');
    localStorage.removeItem('required_setting');
    navigate('/login', { replace: true });
  }, [navigate]);

  const closeAutoLogoutModal = useCallback(() => {
    destroySession();
  }, [destroySession]);

  const closeStartModal = useCallback((action) => {
    let required_setting;
    const required_setting_string = localStorage.getItem('required_setting');
    if (required_setting_string) {
      required_setting = required_setting_string.split(',');
    } else {
      required_setting = [];
    }
    setStartPage(false);

    if (action === 'logout') {
      destroySession();
      return;
    }

    if (required_setting.includes('password')) {
      setTimeout(() => {
        setMCP(true);
        setExpirationPass(true);
      }, 1500);
    }
  }, [destroySession]);

  const closeChangePasswordModal = useCallback(() => {
    setMCP(false);
  }, []);

  useEffect(() => {
    requireLogin();
    checkClaritySettings();
  }, [requireLogin, checkClaritySettings]);

  useEffect(() => {
    if (!isActiveTimer) {
      const events = ['load', 'mousedown', 'click', 'scroll', 'keypress'];

      events.forEach((event) => {
        window.addEventListener(event, onRefreshStart);
      });

      return () => {
        events.forEach((event) => {
          window.removeEventListener(event, onRefreshStart);
        });
        onRefreshStop();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActiveTimer, onRefreshStart]);

  useEffect(() => {
    const intervalId = setInterval(refreshToken, REFRESH_TIME);
    return () => { return clearInterval(intervalId); };
  }, [refreshToken]);

  const getAppClassName = useCallback(() => {
    const isMain = path === '/main';
    const isResult = path.indexOf('result') !== -1;
    const isSettings = path.indexOf('settings') !== -1 || path.indexOf('systems') !== -1;
    const isMyPage = path.indexOf('my_page') !== -1 || path.indexOf('knowledge_base/cve_code') !== -1;

    return cx(
      'app',
      { dashboard: isMain },
      { result: !isMain },
      { result_all_files: isResult },
      { has_left_menu: isResult },
      { setting: isSettings },
      { setting_policy: path.indexOf('settings/policy') !== -1 },
      { setting_project: path.indexOf('settings/project') !== -1 || path.indexOf('settings/user') !== -1 || path.indexOf('settings/defined') !== -1 },
      { profile: path.indexOf('about') !== -1 || path.indexOf('profile') !== -1 },
      { profile_detail: path.indexOf('about') !== -1 || path.indexOf('profile') !== -1 },
      { setting_type_2: path.indexOf('systems') !== -1 },
      { my_bookmarks: path.indexOf('my_page/bookmark') !== -1 },
      { my_issues: path.indexOf('my_page/issue') !== -1 },
      { my: isMyPage }
    );
  }, [path]);

  return (
    <div className={getAppClassName()}>
      <Header />
      <div className="container">
        <Outlet />
        <UploadingTools />
      </div>
      <AutoLogoutModal
        //isOpenMAL={isOpenMAL}
        isOpenMAL={false}
        closeModal={closeAutoLogoutModal}
      />
      <StartPageModal
        isOpenStartPage={isOpenStartPage}
        closeModal={closeStartModal}
      />
      <ChangePasswordModal
        isOpenMCP={isOpenMCP}
        isExpirationPass={isExpirationPass}
        closeModal={closeChangePasswordModal}
      />
    </div>
  );
}

export default Layout;
